import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2026 semantics GmbH
//  Date      : $Date: 2026-07-28 15:53:28 +0200 (Tue, 28 Jul 2026) $
//  Author    : Olli Powroznik
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 164668 $
//  Purpose   : refid highlight overlay — hover/click styling + margin box + XMenu
// =============================================================================
// ---Third-party imports
import React from 'react';
import { createPortal } from 'react-dom';
// ---Framework imports
import { useEffect, useState } from 'uix/hooks';
import { XMenu } from 'uix/components';
import { callIfFunc } from 'ldash';
// ---Local imports
import S from './editOverlay.css';
import { Menu } from 'semantic-ui-react';
// * measures a DOM element reactively (viewport coords, for fixed-position portals)
export const useElementRect = (el)=>{
    let [rect, setRect] = useState(null);
    useEffect(()=>{
        if (!el) {
            setRect(null);
            return;
        }
        const measure = ()=>{
            if (!el.isConnected) {
                setRect(null);
                return;
            }
            let cs = getComputedStyle(el);
            let m = (side)=>el.style[side] ? parseFloat(cs[side]) || 0 : 0;
            let mt = m('marginTop'), mr = m('marginRight'), mb = m('marginBottom'), ml = m('marginLeft');
            let r = el.getBoundingClientRect();
            setRect({
                mt,
                mr,
                mb,
                ml,
                fixedTop: r.top - mt,
                fixedLeft: r.left - ml,
                fixedWidth: r.width + ml + mr,
                fixedHeight: r.height + mt + mb,
                elFixedTop: r.top,
                elFixedLeft: r.left,
                elFixedRight: r.right,
                elFixedWidth: r.width,
                elFixedHeight: r.height
            });
        };
        measure();
        let ro = new ResizeObserver(measure);
        ro.observe(el);
        let mo = new MutationObserver(measure);
        mo.observe(el, {
            attributes: true,
            attributeFilter: [
                'style'
            ]
        });
        window.addEventListener('scroll', measure, true);
        window.addEventListener('resize', measure);
        return ()=>{
            ro.disconnect();
            mo.disconnect();
            window.removeEventListener('scroll', measure, true);
            window.removeEventListener('resize', measure);
        };
    }, [
        el
    ]);
    return rect;
};
// * renders margin box, highlight border and XMenu (all portaled) for one element
export const ElementHighlight = ({ el, showMenu, active, onRefIdClick })=>{
    let rect = useElementRect(el);
    if (!rect) return null;
    let { mt, mr, mb, ml } = rect;
    let hasMargin = Boolean(mt || mr || mb || ml);
    let refid = el.getAttribute('refid');
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            hasMargin && /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx("span", {
                className: S.marginBoxFixed,
                style: {
                    top: rect.fixedTop,
                    left: rect.fixedLeft,
                    width: rect.fixedWidth,
                    height: rect.fixedHeight
                }
            }), document.body),
            /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx("span", {
                className: S.highlightFixed,
                style: {
                    top: rect.elFixedTop,
                    left: rect.elFixedLeft,
                    width: rect.elFixedWidth,
                    height: rect.elFixedHeight
                }
            }), document.body),
            (showMenu || active) && /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx("div", {
                "data-refid-overlay": "true",
                className: S.menuAnchorFixed,
                style: {
                    top: rect.elFixedTop,
                    left: rect.elFixedRight
                },
                children: /*#__PURE__*/ _jsx(XMenu, {
                    inverted: true,
                    color: "blue",
                    attached: "bottom",
                    size: "tiny",
                    children: /*#__PURE__*/ _jsx(XMenu.Item, {
                        style: {
                            backgroundColor: 'white',
                            color: 'var(--editOverlay_highlight_color)'
                        },
                        active: active,
                        icon: active ? 'close' : 'pencil',
                        onClick: ()=>onRefIdClick(active ? null : refid)
                    })
                })
            }), document.body)
        ]
    });
};
// * renders highlights for hovered and/or pinned elements
export const RefIdHighlightLayer = ({ hover, activeEl, editable, onRefIdClick })=>{
    // dedup by refid, not node identity — a refid can appear on multiple nodes
    let byRefid = new Map();
    for (let el of [
        hover,
        activeEl
    ].filter(Boolean)){
        let id = el.getAttribute('refid');
        if (!byRefid.has(id)) byRefid.set(id, el);
    }
    let elements = [
        ...byRefid.values()
    ];
    if (!elements.length) return null;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: elements.map((el)=>/*#__PURE__*/ _jsx(ElementHighlight, {
                el: el,
                showMenu: el === hover && editable,
                active: el === activeEl,
                onRefIdClick: onRefIdClick
            }, el.getAttribute('refid')))
    });
};
// * refid hover/click highlighting and pinning — state-only hook
export const useRefIdHighlight = ({ activeRefId, editable, scopeId, onDeselect, onSelect } = {})=>{
    let [hover, setHover] = useState(null);
    let [activeEl, setActiveEl] = useState(null);
    // * resolve activeRefId → DOM element
    useEffect(()=>{
        if (!activeRefId) {
            setActiveEl(null);
            return;
        }
        let scope = scopeId ? document.getElementById(scopeId) : document;
        let el = scope?.querySelector(`[refid="${CSS.escape(activeRefId)}"]`);
        setActiveEl(el || null);
    }, [
        activeRefId,
        scopeId
    ]);
    // * clicking outside the active widget and outside its overlay/property editor UI deselects it
    useEffect(()=>{
        if (!editable || !activeEl) return;
        let onOutsideClick = (e)=>{
            if (activeEl.contains(e.target)) return;
            if (e.target.closest('[data-refid-overlay], [data-refid-editor], .refidEditorDialog, .ui.popup, .reflex-splitter')) return;
            callIfFunc(onDeselect);
        };
        document.addEventListener('mousedown', onOutsideClick, true);
        return ()=>document.removeEventListener('mousedown', onOutsideClick, true);
    }, [
        editable,
        activeEl,
        onDeselect
    ]);
    const onMouseOver = (e)=>{
        if (e.target.closest('[data-refid-overlay]')) return;
        if (e.relatedTarget?.closest('[data-refid-overlay]')) return;
        let el = e.target.closest('[refid]');
        if (el === hover) return;
        setHover(el || null);
    };
    const onMouseOut = (e)=>{
        if (e.relatedTarget?.closest('[data-refid-overlay]')) return;
        if (!e.target.closest('[refid]')) setHover(null);
    };
    // * clicking a widget activates it directly - the hover pencil stays as a discoverable affordance
    const onClick = (e)=>{
        if (e.target.closest('[data-refid-overlay]')) return;
        let el = e.target.closest('[refid]');
        if (!el) return;
        let refid = el.getAttribute('refid');
        if (refid !== activeRefId) callIfFunc(onSelect, refid);
    };
    if (!editable) return {
        hover: null,
        activeEl: null
    };
    return {
        onMouseOver,
        onMouseOut,
        onClick,
        hover,
        activeEl
    };
};
